/**
 * N2InfluencerPage — the "For content partners" B2B destination at /influencer.
 *
 * Creators serving retail traders — YouTube, X, newsletters, Discord — who
 * want to bring their audience to serious tools. We do the research and
 * platform; they get white-label + revenue share. CTA is "Partner with us"
 * (contact-sales), by-application while the program is being scaled.
 */

import React from 'react';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import {
  SectionLabel, PrimaryButton, SecondaryButton, Eyebrow, PageTitle, PageLede,
  SectionHead, Body,
} from './N2Primitives';

export const N2InfluencerPage: React.FC = () => (
  <N2Frame page="influencer">
    <Hero />
    <WhatYouGetSection />
    <RevenueSection />
    <ProofSection />
    <HowItWorksSection />
    <ContactCTA />
  </N2Frame>
);

const Hero: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 48px',
    textAlign: 'center',
  }}>
    <Eyebrow>For content partners</Eyebrow>
    <PageTitle italic="the tools.">
      You bring the audience. We bring
    </PageTitle>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageLede>
        Creators serving retail traders — YouTube, X, newsletters, Discord —
        who want to graduate their audience from opinion to actual research.
        White-label our platform under your brand and earn revenue share on
        every subscriber your audience brings.
      </PageLede>
    </div>
    <div style={{
      display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap',
    }}>
      <PrimaryButton to="/contact?source=n2-influencer">Partner with us</PrimaryButton>
      <SecondaryButton to="/n2/briefings">See what your audience gets</SecondaryButton>
    </div>
  </section>
);

// ---- What you get --------------------------------------------------------

const CAPABILITIES = [
  {
    label: 'White-label',
    title: 'Your brand, our tools',
    body: 'Your logo, your domain, your voice on the wrapper. The research surface underneath is the same one Notches customers use — published methodology, live scorecard, daily briefings. Your audience never sees "powered by WFS" unless you want them to.',
  },
  {
    label: 'Revenue share',
    title: 'Paid on every subscriber',
    body: 'You get a percentage of every subscription your audience creates through your channel — Premier, per-symbol, all of it. Recurring, not one-time. Tracked per-referrer, paid monthly.',
  },
  {
    label: 'Content you can point to',
    title: 'Weekly research + daily briefings',
    body: 'The public research (Notches Weekly, daily briefings, scorecard) is the credibility layer for your audience — you\'re not selling ideas, you\'re pointing at a real institution. Fresh content every week, no calendar pressure on you.',
  },
  {
    label: 'No content lift on you',
    title: 'Focus on the audience, not the tools',
    body: 'You keep doing what you do — audience building, community, education. We handle the models, the infrastructure, the customer support, the compliance. Your job is one thing: bring the audience.',
  },
];

const WhatYouGetSection: React.FC = () => (
  <section style={{
    background: palette.paper2,
    borderTop: `1px solid ${palette.line}`, borderBottom: `1px solid ${palette.line}`,
    padding: '80px 32px', marginTop: 32,
  }}>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <SectionLabel left="What you get" right="not another affiliate program" />
      <div style={{ marginTop: 8, marginBottom: 40 }}>
        <SectionHead italic="serious.">Your audience gets</SectionHead>
        <Body>
          The upgrade path from "watch this video" to "here's the research
          that goes with it" — under your brand, on our platform.
        </Body>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20,
      }} className="n2-caps">
        {CAPABILITIES.map((c) => (
          <div key={c.label} style={{
            background: palette.paper, border: `1px solid ${palette.line}`,
            borderRadius: 4, padding: '28px 28px 24px', position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 28, width: 28, height: 3,
              background: palette.gold,
            }} />
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: palette.goldDeep, marginBottom: 12,
            }}>{c.label}</div>
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 22,
              lineHeight: 1.25, margin: '0 0 10px', color: palette.ink,
              letterSpacing: '-0.01em',
            }}>{c.title}</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.6,
              color: palette.ink2, margin: 0,
            }}>{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- Revenue framing -----------------------------------------------------

const RevenueSection: React.FC = () => (
  <section style={{ maxWidth: 1100, margin: '0 auto', padding: '96px 32px 0' }}>
    <SectionLabel left="Revenue share" right="scoped in the partnership call" />
    <div style={{ marginTop: 8, marginBottom: 32 }}>
      <SectionHead italic="not per click.">You get paid per subscriber,</SectionHead>
      <Body>
        The economics are audience-specific — a 200k YouTube subscriber list
        with a 2% conversion rate has different math than a 5k newsletter
        with a 15% conversion rate. Both work; the split is set in the
        partnership call after we look at your audience.
      </Body>
    </div>
    <div style={{
      background: palette.paper, border: `1px solid ${palette.line}`,
      borderRadius: 4, padding: '28px 32px',
    }}>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: palette.goldDeep, marginBottom: 12,
      }}>What we don't do</div>
      <ul style={{
        listStyle: 'none', margin: 0, padding: 0,
      }}>
        {[
          'No one-time bounty payments (recurring only, so incentives line up)',
          'No cookie-tracked links to random landing pages (per-referrer accounts, so you can prove your audience volume)',
          'No exclusivity locks (partner with whoever else you want)',
          'No forced upsells (we don\'t email your list, ever)',
        ].map((s, i) => (
          <li key={i} style={{
            fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.55,
            color: palette.ink2, padding: '8px 0',
            borderTop: i > 0 ? `1px dashed ${palette.line}` : 'none',
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <span style={{ color: palette.goldDeep, marginTop: 2 }}>×</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// ---- Proof — link to real published surfaces ----------------------------

const ProofSection: React.FC = () => (
  <section style={{ maxWidth: 1100, margin: '0 auto', padding: '96px 32px 0' }}>
    <SectionLabel left="What your audience will see" right="the research is real" />
    <div style={{ marginTop: 8, marginBottom: 40 }}>
      <SectionHead italic="already ships.">The product</SectionHead>
      <Body>
        Read the surfaces your audience will land on. If the shape of the
        output looks like something you'd be comfortable pointing at from
        your channel, come talk.
      </Body>
    </div>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
    }}>
      {[
        { to: '/n2/briefings', label: 'Daily briefings', desc: 'Twice-daily read on regime + positioning' },
        { to: '/n2/scorecard', label: 'Public scorecard', desc: 'Every reading published, losses named' },
        { to: '/n2/research',  label: 'Weekly letter',    desc: 'Fresh long-form research every Sunday' },
      ].map((p) => (
        <a key={p.to} href={p.to} style={{
          textDecoration: 'none', color: 'inherit',
          padding: '22px 22px 20px', border: `1px solid ${palette.line}`,
          background: palette.paper, borderRadius: 4,
          display: 'block', transition: 'border-color 120ms',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = palette.goldDeep)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = palette.line)}>
          <div style={{
            fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 500,
            color: palette.ink, marginBottom: 6,
          }}>{p.label}</div>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
          }}>{p.desc}</div>
        </a>
      ))}
    </div>
  </section>
);

// ---- How it works --------------------------------------------------------

const HowItWorksSection: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 0',
  }}>
    <SectionLabel left="How it works" right="three steps to launch" />
    <div style={{ marginTop: 40 }}>
      {[
        { step: 'I', title: 'Partnership call', body: 'You describe your audience — platform, size, engagement, what you\'re teaching them. We describe the revenue-share math for that shape of audience, and what content patterns actually convert (based on what\'s worked for other partners).' },
        { step: 'II', title: 'Branded setup', body: 'We spin up your white-label surface with your brand and voice, on a subdomain you point at. Per-referrer tracking is wired so every signup from your audience is attributed correctly. Usually 1-2 weeks from call to live.' },
        { step: 'III', title: 'Launch + recurring payout', body: 'You point your audience at the new surface. Revenue-share paid monthly, per subscriber. You keep making the content you make; we handle everything downstream.' },
      ].map((s, i) => (
        <div key={s.step} style={{
          display: 'grid', gridTemplateColumns: '80px 1fr', gap: 32,
          padding: '24px 0',
          borderBottom: i < 2 ? `1px solid ${palette.line}` : 'none',
        }}>
          <div style={{
            fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 40,
            color: palette.goldDeep, textAlign: 'center', lineHeight: 1,
          }}>{s.step}</div>
          <div>
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 24,
              margin: '0 0 8px', color: palette.ink, letterSpacing: '-0.01em',
            }}>{s.title}</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.6,
              color: palette.ink2, margin: 0,
            }}>{s.body}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ---- Contact CTA --------------------------------------------------------

const ContactCTA: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 96px',
    textAlign: 'center',
  }}>
    <SectionHead italic="together.">Let's grow this</SectionHead>
    <div style={{ maxWidth: 560, margin: '0 auto 24px' }}>
      <Body>
        Partnership program is by application while we scale. Tell us about
        your audience and we'll come back with a real proposal within 48 hours.
      </Body>
    </div>
    <PrimaryButton to="/contact?source=n2-influencer">Partner with us</PrimaryButton>
  </section>
);

export default N2InfluencerPage;
